import { CaretRightIcon, CheckIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import type { ReactNode } from "react";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import useBreakpoints from "../../../hooks/useBreakpoints";
import type { BookSelectionStep } from "./useBookSelectionSteps";

interface BookSelectionStepperItem {
    step: BookSelectionStep;
    /** The step's name until something is chosen, then the choice. */
    label: string;
    isDone: boolean;
    isReachable: boolean;
    /** What the step asks for, e.g. a pin for the federal state; shown below LG until it's done. */
    icon: ReactNode;
    /** The choice in miniature, e.g. the state's flag; shown below LG once the step is done. */
    pickedIcon?: ReactNode;
}

interface BookSelectionStepperProps {
    items: Array<BookSelectionStepperItem>;
    activeStep: BookSelectionStep;
    onStepClick: (step: BookSelectionStep) => void;
}

const getCompactMarkerColors = (isPicked: boolean, isActive: boolean) => {
    if (isPicked) {
        return "bg-white text-p6-grey-dark ring-1 ring-transparent-black";
    }
    return isActive ? "bg-creation-primary text-white" : "bg-background-light text-p6-grey-mid";
};

/**
 * Finished steps show their choice and lead back to it. Below LG the labels wouldn't fit, so each step is a
 * thumb-sized marker instead: what it asks for until it's done, then the choice itself.
 */
const BookSelectionStepper = (props: BookSelectionStepperProps) => {
    const { isAboveLG } = useBreakpoints();
    const t = useT();

    const renderMarker = (item: BookSelectionStepperItem, isActive: boolean, isFinished: boolean) => {
        if (isAboveLG) {
            return (
                <span
                    aria-hidden="true"
                    className={`flex size-m shrink-0 items-center justify-center rounded-circle text-s font-bold ${
                        isActive || item.isDone
                            ? "bg-creation-primary text-white"
                            : "bg-background-light text-p6-grey-mid"
                    }`}
                >
                    {isFinished ? <CheckIcon weight="bold" /> : item.step}
                </span>
            );
        }

        const isPicked = item.isDone && !!item.pickedIcon;
        return (
            <span
                aria-hidden="true"
                className={`flex size-xl shrink-0 items-center justify-center overflow-hidden rounded-circle text-xl font-bold ${getCompactMarkerColors(
                    isPicked,
                    isActive
                )} ${isActive ? "outline-2 outline-offset-2 outline-creation-primary" : ""}`}
            >
                {isPicked ? item.pickedIcon : item.icon}
            </span>
        );
    };

    return (
        <ol
            className={`m-no flex list-none items-center p-no ${
                isAboveLG
                    ? "overflow-hidden rounded-medium border border-solid border-p6-grey-light"
                    : "justify-center gap-xs"
            }`}
            data-testid="book-selection-stepper"
        >
            {props.items.map((item, index) => {
                const isActive = item.step === props.activeStep;
                const isFinished = item.isDone && !isActive;
                const activeBackground = isActive ? "bg-creation-primary-light-pastel" : "bg-white";

                return (
                    <li
                        key={item.step}
                        className={`flex min-w-no items-center gap-xxs ${isAboveLG ? "flex-1" : ""}`}
                    >
                        {index > 0 && (
                            <CaretRightIcon
                                aria-hidden="true"
                                className="shrink-0 text-p6-grey-light"
                            />
                        )}
                        <button
                            type="button"
                            onClick={() => props.onStepClick(item.step)}
                            disabled={!item.isReachable}
                            aria-current={isActive ? "step" : undefined}
                            aria-label={isAboveLG ? undefined : item.label}
                            className={`flex min-w-no flex-1 items-center gap-xs rounded-small-container border-none text-left ${
                                isAboveLG ? `p-s ${activeBackground}` : "bg-transparent p-xxs"
                            } ${
                                !item.isReachable
                                    ? "cursor-not-allowed"
                                    : isActive
                                    ? "cursor-default"
                                    : "cursor-pointer"
                            }`}
                            data-testid={`book-selection-step-${item.step}`}
                        >
                            {renderMarker(item, isActive, isFinished)}
                            {isAboveLG && (
                                <span className="flex min-w-no flex-col">
                                    <span
                                        className={`text-xs uppercase tracking-wider ${
                                            isActive ? "text-creation-primary" : "text-p6-grey-mid"
                                        }`}
                                    >
                                        {t("Step {number}", { _tags: "CreateTest,SelectBook", number: item.step })}
                                    </span>
                                    <span className="flex min-w-no items-center gap-xxs">
                                        <span
                                            className={`truncate text-m font-medium ${
                                                isActive
                                                    ? "text-creation-primary"
                                                    : item.isDone
                                                    ? "text-p6-grey-dark"
                                                    : "text-p6-grey-mid"
                                            }`}
                                        >
                                            {item.label}
                                        </span>
                                        {isFinished && (
                                            <PhaseSixIcon
                                                name="edit-avatar"
                                                className="shrink-0 text-l text-p6-grey-dark"
                                            />
                                        )}
                                    </span>
                                </span>
                            )}
                        </button>
                    </li>
                );
            })}
        </ol>
    );
};

export default BookSelectionStepper;
